<template>
  <v-page page-class="schedule">
    <toolbar align="left">
      <template v-slot:right>
        <transition name="fade">
          <v-button
            v-if="!isViewingToday"
            class="v-toolbar__button"
            secondary
            ghost
            round
            @click="goToToday"
          >
            <v-icon icon="event_repeat" size="1.1rem"></v-icon>
          </v-button>
        </transition>

        <div id="page-toolbar-actions" />
      </template>

      <template v-slot:append>
        <div class="schedule__header bg-white">
          <div
            class="container d-flex justify-content-between align-items-center"
          >
            <v-button class="schedule__nav-button" round ghost @click="goBack">
              <v-icon icon="chevron_left" />
            </v-button>
            <h3 class="text-grey-80 text-center my-2">
              {{
                isViewingToday ? "Today" : toDate(currentDate, "dddd, D MMM")
              }}
            </h3>
            <v-button
              class="schedule__nav-button ml-0 pl-0 pr-0"
              round
              ghost
              @click="goForward"
            >
              <v-icon icon="chevron_right" />
            </v-button>
          </div>

          <div class="container">
            <div ref="calendar"></div>
          </div>
        </div>
      </template>
    </toolbar>

    <div class="container px-2 pt-3 pb-4 mb-5">
      <transition
        :name="
          animationDirection === ANIMATION_DIRECTIONS.FORWARD
            ? 'fade-out-left'
            : 'fade-out-right'
        "
        mode="out-in"
      >
        <div :key="String(currentDate)" class="schedule__list">
          <template v-if="Object.keys(currentItems).length">
            <div
              v-for="contextId in currentItemsOrderedKeys"
              class="pb-3 border-top"
              :key="contextId"
            >
              <div
                class="schedule__seperator"
                v-if="
                  contextId === CONTEXTS.GLOBAL.id &&
                    Object.keys(currentItems).length > 1
                "
              />
              <v-link
                :to="contexts[contextId].path"
                class="schedule__context-group-header d-flex align-items-center justify-content-between mb-2"
              >
                <h4 class="text-grey-80 my-0">
                  {{ contexts[contextId].title }}
                </h4>
              </v-link>

              <div
                v-for="item in currentItems[contextId]"
                :key="item.id"
                class="d-flex align-items-center mb-2"
              >
                <collection-item
                  class="w-100"
                  :to="item.data.path"
                  :page-id="item.type === 'page' ? item.data.id : null"
                  :title="item.data.title"
                  :type="item.type"
                  :content-type="item.content_type"
                  :image="item.data.banner"
                  :video="item.data.video"
                  :item-data="item.data || {}"
                  card-display="horizontal"
                  card-style="raised"
                  :show-countdown="false"
                  unlock
                  show-only-time
                />
                <div
                  v-if="canEdit(item.scheduled_item)"
                  class="pl-1 mr-n1"
                  @click="openScheduleActionsModal(item.scheduled_item)"
                >
                  <v-icon
                    icon="more_vert"
                    size="1.5rem"
                    class="schedule-actions__toggle"
                  />
                </div>
              </div>
            </div>
          </template>
          <template v-else-if="isLoading">
            <div class="d-flex justify-content-center p-5">
              <v-loader />
            </div>
          </template>
          <template v-else-if="!Object.keys(currentItems).length">
            <div class="text-center py-5">
              <v-icon
                class="text-grey-40"
                icon="calendar_today"
                size="2.5rem"
              ></v-icon>
              <p class="my-2 text-grey-70 font-weight-bold">
                Nothing scheduled for
                {{ toDate(currentDate, "dddd") }}
              </p>
            </div>
          </template>
        </div>
      </transition>
    </div>

    <page-action-panel-modal page-id="schedule" />

    <v-modal
      v-if="selectedScheduledItem"
      short
      :x-short="!isEditing"
      @close="resetActionsState"
    >
      <template :slot="scheduleActionsHash">
        <transition name="fade-out-left" mode="out-in">
          <action-header
            v-if="!updatedScheduledItem"
            :key="
              isDuplicating
                ? 'duplicate'
                : isRescheduling
                ? 'reschedule'
                : 'update'
            "
          >
            {{
              isDuplicating
                ? "Duplicate"
                : isRescheduling
                ? "Reschedule"
                : "Update"
            }}
            {{ selectedScheduledItem.item.data.title }}
          </action-header>
        </transition>
        <action-body>
          <transition name="fade-out-left" mode="out-in">
            <icon-message
              v-if="updatedScheduledItem"
              icon="done_all"
              :heading="`Successfully ${editingType}`"
              :description="
                `${
                  selectedScheduledItem.item.data.title
                } has been ${editingType.toLowerCase()} to ${toDate(
                  selectingDate
                )}.`
              "
            />
            <div v-else-if="isEditing" class="px-2 pt-1 pb-2">
              <schedule-input
                :date="selectingDate"
                :page-content-type="selectingContentType"
                :heading="editingHeading"
                @input="value => (selectingDate = value)"
              />
            </div>
            <template v-else>
              <div key="actions" class="text-left">
                <v-button
                  v-if="canReschedule(selectedScheduledItem)"
                  ghost
                  secondary
                  block
                  @click="() => (isRescheduling = true)"
                >
                  <v-icon class="mr-1" icon="event" />
                  Reschedule
                </v-button>
                <v-button
                  v-if="canDuplicate(selectedScheduledItem)"
                  ghost
                  secondary
                  block
                  @click="() => (isDuplicating = true)"
                >
                  <v-icon class="mr-1" icon="control_point_duplicate" />
                  Duplicate to another day
                </v-button>
                <v-button
                  ghost
                  secondary
                  block
                  @click="() => (showRemoveConfirm = true)"
                >
                  <v-icon class="mr-1" icon="cancel" />
                  Remove from schedule
                </v-button>
              </div>
            </template>
          </transition>
        </action-body>
        <action-footer
          v-if="isEditing"
          classes="w-100 d-flex justify-content-between"
        >
          <v-button class="flex-shrink-0" ghost @click="handleCancelEditing">
            Back
          </v-button>
          <v-button
            v-if="updatedScheduledItem"
            block
            @click="handleViewSchedule"
          >
            View in Schedule
          </v-button>
          <v-button
            class="flex-1"
            block
            :is-loading="isUpdatingSchedule"
            v-else
            @click="handleRescheduleOrDuplicate"
          >
            {{ isDuplicating ? "Duplicate" : "Reschedule" }}
          </v-button>
        </action-footer>
      </template>
    </v-modal>

    <!-- TODO: Extract alert -->
    <alert-dialog
      :key="_uid"
      title="Are you sure you want to remove this date?"
      :actions="[
        {
          title: 'Cancel',
          callback: closeRemoveConfirm
        },
        {
          title: 'Yes, remove',
          callback: confirmRemoveFromSchedule
        }
      ]"
      :show="showRemoveConfirm"
      @close="closeRemoveConfirm"
    >
      It will no longer appear on your schedule.
    </alert-dialog>
  </v-page>
</template>

<script>
import { mapActions, mapGetters, mapState, mapMutations } from "vuex"
import { eventcalendar } from "@mobiscroll/javascript"
import "@mobiscroll/javascript/dist/css/mobiscroll.scss"

import { ACTIONS } from "@/store/page-action-panel"

import { isPresent } from "@/lib/utils"
import {
  toDate,
  add,
  subtract,
  isSameDay,
  daysBetweenDates,
  dayOfMonth,
  isPast
} from "@/lib/date-utils"

import {
  formatDate,
  VIEW_TYPES,
  INITIAL_VIEW,
  TODAY,
  ANIMATION_DIRECTIONS,
  INDEXED_DATE_FORMAT,
  CONTEXTS,
  SCOPE_TYPES
} from "@/lib/schedule-utils"

import CollectionItem from "@/components/CollectionItem"
import Toolbar from "@/components/Toolbar"

export default {
  components: {
    Toolbar,
    CollectionItem,
    ActionHeader: () => import("@/components/modals/shared/ActionHeader"),
    ActionBody: () => import("@/components/modals/shared/ActionBody"),
    ActionFooter: () => import("@/components/modals/shared/ActionFooter"),
    AlertDialog: () => import("@/components/AlertDialog"),
    ScheduleInput: () => import("@/components/schedule/ScheduleInput"),
    IconMessage: () => import("@/components/modals/shared/IconMessage"),
    PageActionPanelModal: () =>
      import("@/components/modals/PageActionPanelModal")
  },

  data() {
    return {
      calendar: null,
      from: null,
      to: null,
      pages: {},
      currentPages: [],
      isLoading: true,
      ancestors: {},

      calendarView: VIEW_TYPES.WEEK,

      animationDirection: ANIMATION_DIRECTIONS.FORWARD,
      hasAnimatedBadges: false,

      currentDate: TODAY,
      items: {},
      indexedItems: {},
      contexts: {},

      // Actions
      selectedScheduledItem: null,
      isRescheduling: false,
      isDuplicating: false,
      selectingDate: null,
      updatedScheduledItem: null,
      removingScheduledItem: null,
      showRemoveConfirm: false,
      isUpdatingSchedule: false,

      ANIMATION_DIRECTIONS,
      CONTEXTS
    }
  },

  mounted() {
    this.addAction({
      uid: ACTIONS.TOGGLE_CALENDAR_VIEW,
      pageId: "schedule",
      icon: "today",
      text: "Toggle Month View",
      callback: () => {
        this.toggleCalendarView()
        this.$router.removeHash()
      }
    })

    this.calendar = eventcalendar(this.$refs["calendar"], {
      actionableEvents: false,
      theme: "material",
      themeVariant: "light",
      clickToCreate: false,
      dragToCreate: false,
      dragToMove: false,
      dragToResize: false,
      dateFormatLong: "MMMM D",
      dataTimezone: "utc",
      dayNamesMin: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
      firstDay: 1,
      view: {
        calendar: { type: INITIAL_VIEW },
        agenda: { type: this.calendarView }
      },

      onSelectedDateChange: e => {
        this.currentDate = e.date
      },

      onPageLoading: data => {
        this.from = data.firstDay
        this.to = data.lastDay

        this.fetchPages(this.from, this.to)
      },

      renderDay: day => {
        const formattedDayDate = formatDate(day.date)

        return `
          <div key="${formattedDayDate}" data-day="${formattedDayDate}" class="schedule__day ${[
          formattedDayDate === formatDate(TODAY) ? "--today" : "",
          formattedDayDate === formatDate(this.currentDate) ? "--selected" : ""
        ].join(" ")}">
            ${dayOfMonth(day.date)}
            <div data-role="day-badge" class="schedule__day-badge ${[
              isPresent(this.indexedItems[formattedDayDate])
                ? "--has-content"
                : "",
              this.isDayComplete(formattedDayDate) ? "--complete" : "",
              this.hasAnimatedBadges ? "--no-anim" : ""
            ].join(" ")}">
              <div class="material-icons-round v-icon">done</div>
            </div>
          </div>
        `
      }
    })

    this.goTo(this.focusedDate)
  },

  activated() {
    this.fetchPages(this.from, this.to)
  },

  computed: {
    ...mapGetters("pages", ["isPageComplete"]),
    ...mapGetters("auth", ["isAuthenticated", "hasCheckedAuthentication"]),
    ...mapState("pages", ["pageCompletions"]),

    focusedDate() {
      return this.$route.query.date ? new Date(this.$route.query.date) : null
    },

    selectingContentType() {
      return (
        this.selectedScheduledItem && this.selectedScheduledItem.content_type
      )
    },

    scheduleActionsHash() {
      return `#item-actions`
    },

    isEditing() {
      return this.isRescheduling || this.isDuplicating
    },

    editingType() {
      return this.isEditing
        ? this.isDuplicating
          ? "Duplicated"
          : "Rescheduled"
        : ""
    },

    scheduleActionsHeading() {
      return `${
        this.isRescheduling
          ? "Reschedule"
          : this.isDuplicating
          ? "Duplicate"
          : "Update"
      } ${this.selectedScheduledItem?.data?.title}`
    },

    currentItemsOrderedKeys() {
      return Object.keys(this.currentItems).sort((a, b) => {
        if (a === CONTEXTS.GLOBAL.id) return 1
        if (b === CONTEXTS.GLOBAL.id) return -1

        if (a === CONTEXTS.SUBSCRIBER.id) return 1
        if (b === CONTEXTS.SUBSCRIBER.id) return -1

        return -1
      })
    },

    currentItems() {
      if (!this.items) return {}

      const formattedDate = formatDate(this.currentDate)
      const itemsInRange = this.indexedItems[formattedDate] || []

      const groupedByContext = itemsInRange.reduce((acc, item) => {
        const context = item.context
          ? item.context
          : item.scope_type && item.scope_type === SCOPE_TYPES.SUBSCRIBER
          ? CONTEXTS.SUBSCRIBER
          : CONTEXTS.GLOBAL

        acc[context.id] = acc[context.id] || []
        acc[context.id].push({
          ...item.item,
          scheduled_item: item
        })

        if (context && !this.contexts[context.id]) {
          this.contexts[context.id] = context
        }

        return acc
      }, {})

      return groupedByContext
    },

    isViewingToday() {
      return isSameDay(new Date(), this.currentDate)
    },

    editingHeading() {
      return this.isDuplicating
        ? `${this.selectedScheduledItem.item.data.title} will appear on both the original date & the new date.`
        : `Move ${this.selectedScheduledItem.item.data.title} to another date, it will only appear on your new date.`
    }
  },

  methods: {
    toDate,

    ...mapActions("schedule", [
      "fetchScheduledItems",
      "rescheduleItem",
      "duplicateScheduledItem",
      "removeScheduledItem"
    ]),
    ...mapMutations("pageActionPanel", ["addAction", "removeAction"]),

    fetchPages(from, to) {
      if (!this.hasCheckedAuthentication) return

      this.isLoading = true

      this.fetchScheduledItems({ from, to })
        .then(({ items }) => {
          this.items = items

          this.indexedItems = {
            ...this.indexedItems,
            // Remove any items that are no longer in the range
            ...daysBetweenDates(from, to, INDEXED_DATE_FORMAT).reduce(
              (acc, date) => {
                acc[date] = []
                return acc
              },
              {}
            ),
            // Overwrite any items that are in the range
            ...this.items.reduce((acc, item) => {
              daysBetweenDates(
                item.starts_at,
                item.ends_at,
                INDEXED_DATE_FORMAT
              ).forEach(date => {
                acc[date] = acc[date] || []
                acc[date].push(item)
              })

              return acc
            }, {})
          }

          this.updateCalendarBadges()
        })
        .finally(() => {
          this.isLoading = false
          setTimeout(() => {
            this.hasAnimatedBadges = true
          }, 500)
        })
    },

    isPastDate(date) {
      return isPast(date) && !isSameDay(date)
    },

    goBack() {
      const newDate = subtract(1, "day", this.currentDate)
      this.calendar.navigate(newDate)
      this.currentDate = newDate
    },

    goForward() {
      const newDate = add(1, "day", this.currentDate)
      this.calendar.navigate(newDate)
      this.currentDate = newDate
    },

    goToToday() {
      const newDate = TODAY
      this.calendar.navigate(newDate)
      this.currentDate = newDate
    },

    goTo(date) {
      if (!isPresent(date) || !this.calendar) return

      this.calendar.navigate(date)
      this.currentDate = date
    },

    updateCalendarBadges() {
      Object.keys(this.indexedItems).forEach(date => {
        const day = this.$refs["calendar"].querySelector(`[data-day="${date}"]`)

        if (day) {
          const badge = day.querySelector("[data-role='day-badge']")
          const items = this.indexedItems[date]

          if (items.length > 0) {
            badge.classList.add("--has-content")
          } else {
            badge.classList.remove("--has-content")
          }

          if (this.isDayComplete(date)) {
            badge.classList.add("--complete")
          } else {
            badge.classList.remove("--complete")
          }
        }
      })
    },

    openScheduleActionsModal(item) {
      this.selectedScheduledItem = item
      this.selectingDate = item.starts_at
      this.$router.push(this.scheduleActionsHash)
    },

    handleRescheduleOrDuplicate() {
      if (!this.selectedScheduledItem) return

      if (this.isPastDate(this.selectingDate)) {
        this.$notify("Cannot schedule in the past")
        return
      }

      this.isUpdatingSchedule = true

      if (this.isRescheduling) {
        this.rescheduleItem({
          scheduledItemId: this.selectedScheduledItem.id,
          date: this.selectingDate
        })
          .then(scheduledItem => {
            this.updatedScheduledItem = scheduledItem
            this.fetchPages(this.from, this.to)
          })
          .finally(() => {
            this.isUpdatingSchedule = false
          })
      } else if (this.isDuplicating) {
        this.duplicateScheduledItem({
          scheduledItemId: this.selectedScheduledItem.id,
          date: this.selectingDate
        })
          .then(scheduledItem => {
            this.updatedScheduledItem = scheduledItem
            this.fetchPages(this.from, this.to)
          })
          .finally(() => {
            this.isUpdatingSchedule = false
          })
      }
    },

    handleViewSchedule() {
      const toDate = new Date(this.updatedScheduledItem.starts_at)

      this.goTo(toDate)
      this.resetActionsState()
    },

    toggleCalendarView() {
      if (!this.calendar) return

      if (this.calendarView === VIEW_TYPES.WEEK) {
        this.calendar.setOptions({
          view: {
            calendar: { type: VIEW_TYPES.MONTH },
            agenda: { type: VIEW_TYPES.MONTH }
          }
        })

        this.calendarView = VIEW_TYPES.MONTH
      } else {
        this.calendar.setOptions({
          view: {
            calendar: { type: VIEW_TYPES.WEEK },
            agenda: { type: VIEW_TYPES.WEEK }
          }
        })

        this.calendarView = VIEW_TYPES.WEEK
      }
    },

    isDayComplete(date) {
      const formattedDate = formatDate(date)
      if (!this.indexedItems[formattedDate]) return false

      const subscriberCompletableItems = this.indexedItems[
        formatDate(date)
      ].filter(
        item =>
          item.item_type === "page" &&
          item.scope_type === SCOPE_TYPES.SUBSCRIBER &&
          item.item.data?.is_completable
      )

      if (subscriberCompletableItems.length === 0) {
        // If there are no items specific to the subscriber, we based their
        // progress off the items the trainer has scheduled to allow a day
        // to still be completed
        const globalCompletableItems = this.indexedItems[
          formatDate(date)
        ].filter(
          item =>
            item.item_type === "page" &&
            item.scope_type === SCOPE_TYPES.APP &&
            item.item.data?.is_completable
        )

        if (globalCompletableItems.length === 0) return false

        return globalCompletableItems.every(item =>
          this.isPageComplete(item.item.data.id)
        )
      } else {
        return subscriberCompletableItems.every(item =>
          this.isPageComplete(item.item.data.id)
        )
      }
    },

    confirmRemoveFromSchedule() {
      this.removeScheduledItem(this.selectedScheduledItem.id).finally(() => {
        this.fetchPages(this.from, this.to)
        this.$notify(`Removed from your schedule`)
        this.closeRemoveConfirm()
        this.$router.removeHash()
      })
    },

    closeRemoveConfirm() {
      this.showRemoveConfirm = false
    },

    resetActionsState() {
      this.selectedScheduledItem = null
      this.handleCancelEditing()
    },

    handleCancelEditing() {
      if (this.updatedScheduledItem) {
        this.$router.removeHash()
        this.selectedScheduledItem = null
      }

      this.isDuplicating = false
      this.isRescheduling = false
      this.selectingDate = null
      this.updatedScheduledItem = null
      this.removingScheduledItem = null
    },

    canReschedule(item) {
      return item.scope_type === SCOPE_TYPES.SUBSCRIBER && !item.is_multiday
    },

    canDuplicate(item) {
      return (
        !item.is_multiday &&
        item.scope_type === SCOPE_TYPES.SUBSCRIBER &&
        item.context_type === null
      )
    },

    canEdit(item) {
      return item.scope_type === SCOPE_TYPES.SUBSCRIBER
    }
  },

  watch: {
    currentDate(newDate, prevDate) {
      this.animationDirection =
        newDate > prevDate
          ? ANIMATION_DIRECTIONS.FORWARD
          : ANIMATION_DIRECTIONS.BACKWARD
    },

    isAuthenticated() {
      this.fetchPages(this.from, this.to)
    },

    focusedDate() {
      this.goTo(this.focusedDate)
    }
  }
}
</script>

<style lang="scss">
@import "@/assets/styles/variables";

.--dark-mode {
  .v-page.schedule {
    .schedule__header {
      border-color: $color-ui--grey-10;
    }
  }
}

.v-page.schedule {
  min-height: var(--app-height);

  .schedule__header {
    border-top: 1px solid $color-ui--grey-30;
    padding-bottom: $size--2;
  }

  .schedule__day {
    position: relative;
    background-color: $color-ui--grey-15;
    width: 2.5rem;
    height: 2.5rem;
    border-radius: 50px;
    display: flex;
    align-items: center;
    justify-content: center;
    line-height: 1.85rem;
    margin: 0.23rem auto;
    padding: 0;
    font-size: $font-size--h6;
    border: 2px solid transparent;
    font-weight: $font-weight--bold;

    &.--selected {
      background: $color-ui--grey-50 !important;
      border-color: $color-ui--grey-50 !important;
      color: $color-ui--white;
    }

    &.--today {
      background: var(--brand-color-hex) !important;
      border-color: var(--brand-color-hex) !important;
      color: $color-ui--white !important;
    }
  }

  .schedule__day-badge {
    position: absolute;
    bottom: -2px;
    right: -2px;
    background: var(--brand-color-hex);
    border: 2px solid $color-ui--white;
    height: $size--3;
    width: $size--3;
    border-radius: 10px;
    opacity: 0;

    .v-icon {
      display: none;
    }

    &.--has-content {
      animation: bounce-in 1s ease forwards;
      animation-delay: 0.2s;

      &.--no-anim {
        animation: none;
        opacity: 1;
      }
    }

    &.--complete {
      background: $color-ui--green;
      height: $size--4;
      width: $size--4;
      bottom: -4px;
      right: -4px;

      .v-icon {
        display: block;
        position: absolute;
        top: -1px;
        left: 1px;
        color: $color-ui--white;
        font-size: 0.8rem;
        font-weight: $font-weight--bold;
      }
    }
  }

  .schedule__nav-button {
    margin: 0 $size--1;

    .v-icon {
      font-size: $size--6;
      color: $color-ui--grey-50;
    }
  }

  .schedule__seperator {
    border-top: 1px solid $color-ui--grey-30;
    width: 90%;
    margin: 0 auto;
    margin-bottom: 1.25rem;
  }

  .schedule__context-group-header {
    .v-icon {
      color: $color-ui--grey-30;
    }
  }

  .v-toolbar {
    .v-toolbar__left {
      padding-left: 0;
      max-width: 72%;

      .v-toolbar-logo {
        max-width: initial;
      }
    }

    .v-toolbar__button {
      margin: 0 !important;

      &:last-child {
        margin-right: 0.25rem !important;
      }
    }
  }

  .mbsc-schedule-date-header,
  .mbsc-calendar-header,
  .mbsc-event-list {
    display: none !important;
  }

  .mbsc-calendar-day,
  .mbsc-calendar-week-day {
    border: none;
    color: $color-ui--grey-90;
  }

  .mbsc-calendar-day-outer {
    opacity: 0.2;
  }

  .schedule-actions__toggle {
    color: $color-ui--grey-40;
    cursor: pointer;
    margin-top: 0.25rem;
  }

  .mbsc-calendar-slide {
    background: $color-ui--white;
  }
}
</style>
